import React, { useState, useRef, useEffect } from 'react';
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { Loader } from 'lucide-react';
import { ABLE_COLORS } from '../constants';
import { sendChatMessage, buildLightContext, buildInventoryContext } from '../utils/chatApi';
import { sanitizeMessage } from '../utils/sanitize';
import { acceptEntitySuggestion, rejectEntitySuggestion } from '../utils/entityApi';
import { getOnboardingStatus, completeOnboarding } from '../utils/memoryApi';
import EntitySuggestionPrompt from './EntitySuggestionPrompt';
import {
  ChatMessage,
  ChatInput,
  ChatEmptyState,
  ChatHeader,
  ClearChatModal,
  TaskCandidates
} from './Chat';

// Generate a session ID for "this time" alias choices
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * ChatPanel - Docked side panel on desktop, overlay on mobile
 * Desktop: Coexists with main content (no blocking overlay)
 * Mobile: Full overlay with backdrop
 */
export default function ChatPanel({
  isOpen,
  onClose,
  tasks,
  filteredTasks,
  capacity,
  viewMode,
  currentTask,
  statusFilter = 'active',
  onMessageSent,
  onToolActions,
  messages,
  setMessages,
  stockedItems,
  shoppingList,
  recipes,
  userState,
  initialMessage,
  onInitialMessageSent,
  // Chat panel sizing (from useUIState)
  isDesktop,
  chatWidth,
  chatIsResizing,
  chatShowResizeHint,
  chatStartResize
}) {
  const { getToken } = useKindeAuth();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingEpisode, setPendingEpisode] = useState(null);
  const [entitySuggestions, setEntitySuggestions] = useState([]);
  const [sessionId] = useState(() => generateSessionId());
  
  // Onboarding state
  const [isOnboarding, setIsOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState('welcome');
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  
  const [taskCandidates, setTaskCandidates] = useState(null);
  const [viewportHeight, setViewportHeight] = useState('100vh');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Handle Escape key to exit full-screen
  useEffect(() => {
    if (!isOpen || !isFullScreen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsFullScreen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, isFullScreen]);

  // Handle mobile keyboard
  useEffect(() => {
    if (!isOpen) return;

    const updateViewportHeight = () => {
      if (window.visualViewport) {
        setViewportHeight(`${window.visualViewport.height}px`);
      } else {
        setViewportHeight(`${window.innerHeight}px`);
      }
    };

    updateViewportHeight();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight);
      window.visualViewport.addEventListener('scroll', updateViewportHeight);
    } else {
      window.addEventListener('resize', updateViewportHeight);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
        window.visualViewport.removeEventListener('scroll', updateViewportHeight);
      } else {
        window.removeEventListener('resize', updateViewportHeight);
      }
    };
  }, [isOpen]);

  // Expose chat width as CSS variable for modal positioning
  useEffect(() => {
    if (isOpen && isDesktop && !isFullScreen) {
      document.documentElement.style.setProperty('--chat-width', `${chatWidth}px`);
    } else {
      document.documentElement.style.setProperty('--chat-width', '0px');
    }
  }, [isOpen, isDesktop, isFullScreen, chatWidth]);

  // Check onboarding status and send welcome message
  useEffect(() => {
    async function checkAndStartOnboarding() {
      if (!isOpen || onboardingChecked || messages.length > 0) return;
      
      try {
        const token = await getToken();
        if (!token) return;
        
        const result = await getOnboardingStatus(token);
        setOnboardingChecked(true);
        
        if (result.success && result.needs_onboarding) {
          setIsOnboarding(true);
          setOnboardingStep('welcome');
          
          // Send empty message to trigger Mabel's welcome
          setIsLoading(true);
          const context = { onboardingStep: 'welcome' };
          
          const response = await sendChatMessage(
            'hi', // Simple trigger
            context,
            [],
            false,
            null,
            token,
            true // isOnboarding flag
          );
          
          const welcomeMessage = {
            role: 'assistant',
            content: response.message,
            timestamp: new Date().toISOString()
          };
          setMessages([welcomeMessage]);
          setIsLoading(false);
        }
      } catch (err) {
        console.warn('Onboarding check failed:', err);
        setOnboardingChecked(true);
        setIsLoading(false);
      }
    }
    
    checkAndStartOnboarding();
  }, [isOpen, onboardingChecked, messages.length, getToken, setMessages]);

  const handleInputFocus = () => {
    setTimeout(() => {
      inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && initialMessage && !isLoading) {
      handleSend(initialMessage);
      onInitialMessageSent?.();
    }
    // Only re-run when the panel opens with a new initialMessage; handleSend
    // and onInitialMessageSent are stable references from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialMessage]);

  const sendSuggestion = (text) => {
    setInput(text);
    setTimeout(() => {
      if (text.trim() && !isLoading) {
        handleSend(text);
      }
    }, 100);
  };

  const handleSend = async (overrideInput) => {
    const messageText = overrideInput || input;
    if (!messageText.trim() || isLoading) return;

    const userMessage = sanitizeMessage(messageText);
    if (!userMessage) return;
    
    setInput('');
    setError(null);

    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const accessToken = await getToken();

      const inventoryContext = (stockedItems || shoppingList)
        ? buildInventoryContext(stockedItems || [], shoppingList || [])
        : null;

      const context = buildLightContext(tasks, capacity, viewMode, currentTask, inventoryContext, statusFilter);
      
      if (userState) {
        context.userState = {
          mood: userState.mood,
          energy: userState.energy,
          intents: userState.intents || [],
          location: userState.location,
          timestamp: userState.timestamp
        };
      }

      // Add recipes context for Mabel's recipe tools
      if (recipes && recipes.length > 0) {
        context.recipes = {
          recipes: recipes,
          pantryItems: stockedItems || []
        };
      }

      const SESSION_TIMEOUT_HOURS = 48;
      const MAX_HISTORY_PAID = 60;
      const MAX_HISTORY_FREE = 20;
      
      const isPaidUser = false;
      const maxHistory = isPaidUser ? MAX_HISTORY_PAID : MAX_HISTORY_FREE;
      
      const lastMessage = messages[messages.length - 1];
      const lastTimestamp = lastMessage?.timestamp ? new Date(lastMessage.timestamp) : null;
      const hoursSinceLastMessage = lastTimestamp 
        ? (Date.now() - lastTimestamp.getTime()) / (1000 * 60 * 60) 
        : Infinity;
      
      const isStaleSession = hoursSinceLastMessage > SESSION_TIMEOUT_HOURS;
      const recentMessages = isStaleSession ? [] : messages.slice(-maxHistory);
      
      const conversationHistory = recentMessages.map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await sendChatMessage(
        userMessage,
        isOnboarding ? { ...context, onboardingStep } : context,
        conversationHistory,
        false,
        pendingEpisode,
        accessToken,
        isOnboarding
      );

      // Handle onboarding tool actions (set_user_name)
      if (isOnboarding && response.toolActions) {
        for (const action of response.toolActions) {
          if (action.tool === 'set_user_name' && action.result.success) {
            // Name was set, move to next onboarding step
            setOnboardingStep('got_name');
            
            // After a couple more exchanges, complete onboarding
            // For now, complete after name is set
            try {
              await completeOnboarding(accessToken);
              setIsOnboarding(false);
            } catch (err) {
              console.warn('Failed to complete onboarding:', err);
            }
          }
        }
      }

      if (response.episodeSuggestion) {
        setPendingEpisode(response.episodeSuggestion);
      } else if (response.episodeCreated) {
        setPendingEpisode(null);
      } else if (pendingEpisode) {
        setPendingEpisode(null);
      }
      
      const aiMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString(),
        suggestedTasks: response.suggestedTasks || []
      };
      setMessages(prev => [...prev, aiMessage]);

      if (response.entitySuggestions && response.entitySuggestions.length > 0) {
        setEntitySuggestions(prev => [...prev, ...response.entitySuggestions]);
      }

      if (response.toolActions && response.toolActions.length > 0) {
        const needsClarification = response.toolActions.find(
          a => a.result?.action === 'needs_clarification' && a.result?.candidates
        );
        if (needsClarification) {
          setTaskCandidates({
            candidates: needsClarification.result.candidates,
            action: needsClarification.tool
          });
        } else {
          setTaskCandidates(null);
        }

        onToolActions?.(response.toolActions);
      }

      onMessageSent?.();

    } catch (err) {
      console.error('Chat error:', err);
      setError('Failed to send message. Make sure the backend is running.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!messages || !setMessages) {
    return null;
  }

  const handleAcceptEntity = async (suggestion, permanent) => {
    try {
      const accessToken = await getToken();
      await acceptEntitySuggestion(
        suggestion.mention,
        suggestion.entityId,
        permanent,
        sessionId,
        accessToken
      );
      setEntitySuggestions(prev => prev.filter(s => s.mention !== suggestion.mention));
      onMessageSent?.();
    } catch (err) {
      console.error('Failed to accept entity suggestion:', err);
      setError('Failed to link entity.');
    }
  };

  const handleRejectEntity = async (suggestion) => {
    try {
      const accessToken = await getToken();
      await rejectEntitySuggestion(suggestion.mention, sessionId, accessToken);
      setEntitySuggestions(prev => prev.filter(s => s.mention !== suggestion.mention));
    } catch (err) {
      console.error('Failed to reject entity suggestion:', err);
      setError('Failed to dismiss suggestion.');
    }
  };

  const handleDismissEntity = (suggestion) => {
    setEntitySuggestions(prev => prev.filter(s => s.mention !== suggestion.mention));
  };

  const handleSelectTaskCandidate = (task) => {
    setInput(`"${task.title}"`);
    setTaskCandidates(null);
    setTimeout(() => {
      handleSend(`"${task.title}"`);
    }, 100);
  };

  const handleClearChat = () => {
    setShowClearModal(true);
  };

  const confirmClearChat = () => {
    setMessages([]);
    setShowClearModal(false);
    setTaskCandidates(null);
    setEntitySuggestions([]);
    setError(null);
  };

  if (!isOpen) return null;

  // Desktop: docked panel, no blocking overlay
  // Mobile: full overlay with backdrop
  const showOverlay = !isDesktop || isFullScreen;
  const panelWidth = isFullScreen ? '100vw' : (isDesktop ? `${chatWidth}px` : '100vw');

  return (
    <>
      {/* Backdrop - only on mobile or fullscreen */}
      {showOverlay && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 999
          }}
          onClick={onClose}
        />
      )}

      {/* Chat Panel */}
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: panelWidth,
        height: isDesktop ? '100vh' : viewportHeight,
        maxHeight: '100dvh',
        backgroundColor: 'white',
        boxShadow: isFullScreen ? 'none' : '-4px 0 20px rgba(0, 0, 0, 0.08)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideIn 0.25s ease-out',
        overflow: 'hidden',
        transition: chatIsResizing ? 'none' : 'width 0.2s ease-out',
        borderLeft: isDesktop && !isFullScreen ? `1px solid ${ABLE_COLORS.border}` : 'none'
      }}>
        {/* Resize Handle - desktop only, not fullscreen */}
        {isDesktop && !isFullScreen && (
          <div
            onMouseDown={chatStartResize}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '8px',
              cursor: 'ew-resize',
              background: chatIsResizing ? `${ABLE_COLORS.primary}15` : 'transparent',
              transition: 'background 0.15s',
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              if (!chatIsResizing) e.currentTarget.style.background = `${ABLE_COLORS.primary}10`;
            }}
            onMouseLeave={(e) => {
              if (!chatIsResizing) e.currentTarget.style.background = 'transparent';
            }}
          >
            {/* Grip dots */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              opacity: 0.3
            }}>
              {[...Array(4)].map((_, i) => (
                <div key={i} style={{
                  width: '3px',
                  height: '3px',
                  borderRadius: '50%',
                  background: ABLE_COLORS.textMuted
                }} />
              ))}
            </div>
            
            {/* First-time hint */}
            {chatShowResizeHint && (
              <div style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: ABLE_COLORS.textPrimary,
                color: 'white',
                padding: '6px 10px',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                pointerEvents: 'none'
              }}>
                ← Drag to resize
                <div style={{
                  position: 'absolute',
                  left: '-5px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 0,
                  height: 0,
                  borderTop: '5px solid transparent',
                  borderBottom: '5px solid transparent',
                  borderRight: `5px solid ${ABLE_COLORS.textPrimary}`
                }} />
              </div>
            )}
          </div>
        )}

        {/* Header */}
        <ChatHeader
          isDesktop={isDesktop}
          isFullScreen={isFullScreen}
          onToggleFullScreen={() => setIsFullScreen(!isFullScreen)}
          onClose={onClose}
          onClearChat={handleClearChat}
        />

        {/* Messages */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          backgroundColor: ABLE_COLORS.bg
        }}>
          {messages.length === 0 && (
            <ChatEmptyState onSendSuggestion={sendSuggestion} />
          )}

          {messages.map((msg, idx) => (
            <ChatMessage key={idx} message={msg} />
          ))}

          {isLoading && (
            <div style={{
              alignSelf: 'flex-start',
              padding: '12px',
              borderRadius: '12px',
              backgroundColor: 'white'
            }}>
              <Loader size={16} className="spin" color={ABLE_COLORS.primary} />
            </div>
          )}

          {error && (
            <div style={{
              padding: '12px',
              borderRadius: '8px',
              backgroundColor: '#FEF2F2',
              color: '#DC2626',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {entitySuggestions.map((suggestion, idx) => (
            <EntitySuggestionPrompt
              key={`${suggestion.mention}-${idx}`}
              mention={suggestion.mention}
              entityName={suggestion.entityName}
              score={suggestion.score}
              onAccept={(permanent) => handleAcceptEntity(suggestion, permanent)}
              onReject={() => handleRejectEntity(suggestion)}
              onDismiss={() => handleDismissEntity(suggestion)}
            />
          ))}

          {taskCandidates && (
            <TaskCandidates
              candidates={taskCandidates.candidates}
              onSelect={handleSelectTaskCandidate}
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Clear Chat Modal */}
        {showClearModal && (
          <ClearChatModal
            onConfirm={confirmClearChat}
            onCancel={() => setShowClearModal(false)}
          />
        )}

        {/* Input */}
        <ChatInput
          value={input}
          onChange={setInput}
          onSend={() => handleSend()}
          onFocus={handleInputFocus}
          isLoading={isLoading}
          inputRef={inputRef}
        />
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
}
